import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { getPushNotificationPreference, setPushNotificationPreference } from '../../services/notificationPreferencesApi';
import { requestPushPermission, deleteCurrentFcmToken } from '../../services/pushNotifications';

const cardStyle = {
  marginBottom: '14px',
  padding: '14px',
  borderRadius: '10px',
  border: '1px solid rgba(201,168,76,0.25)',
  background: 'rgba(201,168,76,0.06)',
};

const rowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
};

const titleStyle = {
  color: '#e8e0d0',
  fontSize: '13px',
  fontWeight: 700,
  margin: '0 0 4px',
};

const hintStyle = {
  color: '#8896a8',
  fontSize: '11px',
  margin: 0,
  lineHeight: 1.4,
};

function ToggleSwitch({ on, disabled, onToggle }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      disabled={disabled}
      onClick={onToggle}
      style={{
        width: '52px',
        height: '30px',
        borderRadius: '999px',
        border: `1px solid ${on ? '#4CAF50' : 'rgba(201,168,76,0.35)'}`,
        background: on ? 'rgba(76,175,80,0.35)' : 'rgba(0,0,0,0.35)',
        position: 'relative',
        cursor: disabled ? 'wait' : 'pointer',
        flexShrink: 0,
        padding: 0,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: '3px',
          left: on ? '25px' : '3px',
          width: '22px',
          height: '22px',
          borderRadius: '50%',
          background: on ? '#4CAF50' : '#8896a8',
          transition: 'left 0.15s ease',
        }}
      />
    </button>
  );
}

export default function PushNotificationToggle() {
  const { t } = useLanguage();
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [statusNote, setStatusNote] = useState('');

  useEffect(() => {
    let cancelled = false;
    getPushNotificationPreference()
      .then((data) => {
        if (!cancelled) setEnabled(Boolean(data.pushNotificationsEnabled));
      })
      .catch(() => {
        if (!cancelled) setStatusNote(t('pushPrefLoadFailed'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [t]);

  const handleToggle = async () => {
    const authToken = localStorage.getItem('developmentexpress_token');
    if (!authToken || busy || loading) return;

    setBusy(true);
    setStatusNote('');
    const next = !enabled;
    try {
      if (next) {
        await setPushNotificationPreference(true);
        await requestPushPermission(authToken);
        const browserDenied = typeof Notification !== 'undefined' && Notification.permission === 'denied';
        if (browserDenied) {
          setStatusNote(t('pushBrowserBlocked'));
          setEnabled(false);
        } else {
          setStatusNote(t('pushEnabledNote'));
          setEnabled(true);
        }

        try {
          const user = JSON.parse(localStorage.getItem('developmentexpress_user') || '{}');
          if (user?.id) {
            localStorage.setItem('developmentexpress_user', JSON.stringify({
              ...user,
              pushNotificationsEnabled: !browserDenied,
            }));
          }
        } catch (_err) {
          // ignore localStorage parse errors
        }
      } else {
        await deleteCurrentFcmToken(authToken);
        await setPushNotificationPreference(false);
        setEnabled(false);
        setStatusNote(t('pushDisabledNote'));

        try {
          const user = JSON.parse(localStorage.getItem('developmentexpress_user') || '{}');
          if (user?.id) {
            localStorage.setItem('developmentexpress_user', JSON.stringify({
              ...user,
              pushNotificationsEnabled: false,
            }));
          }
        } catch (_err) {
          // ignore localStorage parse errors
        }
      }
    } catch (err) {
      const msg = String(err.message || '');
      if (msg.includes('20260620') || msg.includes('push_notifications')) {
        setStatusNote(t('pushMigrationRequired'));
      } else {
        setStatusNote(msg || t('pushPrefUpdateFailed'));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={cardStyle}>
      <div style={rowStyle}>
        <div style={{ minWidth: 0 }}>
          <p style={titleStyle}>{String.fromCodePoint(0x1F514)} {t('pushNotificationsTitle')}</p>
          <p style={hintStyle}>{t('pushNotificationsHint')}</p>
        </div>
        <ToggleSwitch on={enabled} disabled={loading || busy} onToggle={handleToggle} />
      </div>
      <p style={{ ...hintStyle, marginTop: '10px', color: enabled ? '#4CAF50' : '#8896a8' }}>
        {loading ? t('loading') : (statusNote || (enabled ? t('pushStatusOn') : t('pushStatusOff')))}
      </p>
    </div>
  );
}
